/**
 * BusinessPagos — el ÚNICO sitio donde la empresa paga a su gente.
 *
 * Reemplaza la nómina anterior, que capturaba y aprobaba pero NUNCA dispersó:
 * su código de pago era un comentario ("coordinar con retiro dual en curso") y
 * solo aceptaba dos destinos — por eso blockchain no aparecía por ningún lado.
 * Esto no es una migración: es terminar lo que quedó a medias.
 *
 * Dos pestañas: PAGAR (pegar la planilla y dispersar) y quién puede recibir.
 * La empresa nunca escribe una dirección de blockchain para alguien ya
 * registrado; y cuando la escribe en la planilla, esa dirección entra en
 * cuarentena la primera vez.
 *
 * Visual: .pagos-ola + tokens --fx-* (glass Ola A, claro + oscuro). Solo chrome.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { downloadTemplate } from '../../lib/business';
import {
  getFreelancers, invitarFreelancer, enviarPrueba, bloquearFreelancer, reenviarEnlaceFreelancer,
  getDispersiones, crearDispersion, previsualizarPlantilla, getLoteDispersion,
  pedirCodigoDispersion, ejecutarDispersion, reintentarDispersion, liberarRetenido,
} from '../../lib/cerebro';

const usd = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* tone → soft rgba via .pagos-ola__chip--* (misma semántica que pasteles antiguos) */
const ESTADO = {
  invitado:     { txt: 'Invitado · falta que registre',  tone: 'muted' },
  por_probar:   { txt: 'Registró · falta la prueba',      tone: 'info' },
  probando:     { txt: 'Prueba enviada · falta que confirme', tone: 'warn' },
  confirmado:   { txt: 'Listo para recibir',              tone: 'good' },
  enfriamiento: { txt: 'Cambió dirección · en espera',    tone: 'warn' },
  bloqueado:    { txt: 'Bloqueado',                       tone: 'bad' },
};

const PASOS = [
  ['pegar', 'Pegar'],
  ['revisar', 'Revisar'],
  ['codigo', 'Código'],
  ['resultado', 'Resultado'],
];

export default function BusinessContratistas() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('pagar');

  const cargar = () => getFreelancers().then(setData)
    .catch((e) => setErr(e?.response?.data?.message || 'No pude cargar los contratistas.'));
  useEffect(() => { cargar(); }, []);

  if (err) return <div className="pagos-ola"><div className="pagos-ola__err">{err}</div></div>;
  if (!data) return <div className="pagos-ola"><div className="pagos-ola__loading">Cargando…</div></div>;

  return (
    <div className="pagos-ola">
      <header className="pagos-ola__head">
        <div>
          <p className="pagos-ola__eyebrow">Tesorería</p>
          <h1 className="pagos-ola__title">Pagos</h1>
          <p className="pagos-ola__sub">
            Págale a tu gente esté donde esté: banco en Colombia, cuenta FlowexPay o billetera.
          </p>
        </div>
        <Link to="/business/pagos-anterior" className="pagos-ola__link-quiet">
          Ver lotes anteriores
        </Link>
      </header>

      <div className="pagos-ola__tabs" role="tablist">
        {[['pagar', 'Pagar'], ['registro', 'Destinos guardados']].map(([k, t]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            onClick={() => setTab(k)}
            className={`pagos-ola__tab${tab === k ? ' pagos-ola__tab--on' : ''}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'registro'
        ? <Registro data={data} recargar={cargar} />
        : <Pagar recargar={cargar} />}
    </div>
  );
}

/* ============ QUIÉN PUEDE RECIBIR ============ */

function Registro({ data, recargar }) {
  const [form, setForm] = useState({ nombre: '', email: '', pais: '', concepto: '' });
  const [link, setLink] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [enviando, setEnviando] = useState('');
  const [linkPrueba, setLinkPrueba] = useState({}); // id -> enlace para reenviar
  const [ok, setOk] = useState(''); // aviso en verde; `msg` es siempre error (rojo)

  async function invitar() {
    setMsg(''); setLink(''); setBusy(true);
    try {
      const r = await invitarFreelancer(form);
      setLink(r.link);
      setForm({ nombre: '', email: '', pais: '', concepto: '' });
      recargar();
    } catch (e) { setMsg(e?.response?.data?.message || 'No pude invitar.'); }
    finally { setBusy(false); }
  }

  async function accion(fn, ...args) {
    setMsg(''); setOk('');
    // El envio de la prueba tarda (es una transaccion real): se marca cual esta
    // en curso para que el boton no invite a darle dos veces.
    if (fn === enviarPrueba) setEnviando(args[0]);
    try {
      const r = await fn(...args);
      // Enviar la prueba y reenviar el enlace rotan el token: el enlace sale UNA
      // vez y la empresa suele necesitarlo para mandarlo por WhatsApp.
      if (r?.link) setLinkPrueba((p) => ({ ...p, [args[0]]: r.link }));
      if (fn === reenviarEnlaceFreelancer) setOk('Listo, le reenviamos el correo con su enlace. El anterior ya no sirve.');
      recargar();
    }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude completarlo.'); }
    finally { setEnviando(''); }
  }

  return (
    <>
      <section className="pagos-ola__panel">
        <h2 className="pagos-ola__h2">Invitar para pagarle por blockchain</h2>
        <p className="pagos-ola__hint">
          Para pagarle en <strong>dólares (USDC) por la red Solana</strong>. Le llega un enlace
          a su nombre donde <strong>él registra su dirección</strong> — tú no la escribes,
          así nadie puede desviarle el pago.
        </p>
        <p className="pagos-ola__hint pagos-ola__hint--tight pagos-ola__hint--xs">
          Si le vas a pagar a un <strong>banco colombiano</strong> o a una <strong>cuenta
          FlowexPay</strong>, no necesitas invitarlo: eso va directo en la planilla.
        </p>
        <div className="pagos-ola__grid-2 pagos-ola__mt">
          {[['nombre', 'Nombre'], ['email', 'Correo'], ['pais', 'País (opcional)'], ['concepto', 'Concepto (ej. diseño)']].map(([k, ph]) => (
            <input key={k} value={form[k]} placeholder={ph}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="pagos-ola__input" />
          ))}
        </div>
        <div className="pagos-ola__alert pagos-ola__alert--info pagos-ola__alert--mt">
          <strong>Lo que le vamos a pedir:</strong> su dirección para recibir <strong>USDC en
          la red Solana</strong> — la que sale de su billetera o exchange (Binance, Phantom,
          Zinli) en «Recibir → USDC → Solana».
          <div className="pagos-ola__mt-sm" style={{ color: '#f87171' }}>
            Si manda una dirección de otra red, el dinero se pierde. Por eso antes del primer
            pago le enviamos US${data.montoPrueba} de prueba y él confirma que le llegó.
          </div>
        </div>

        <button type="button" onClick={invitar} disabled={!form.nombre || !form.email || busy}
          className="pagos-ola__btn pagos-ola__btn--primary pagos-ola__btn--mt">
          {busy ? 'Enviando…' : 'Enviar invitación'}
        </button>
        {link && (
          <div className="pagos-ola__alert pagos-ola__alert--ok pagos-ola__alert--mt">
            <p className="pagos-ola__alert-title">Invitación enviada por correo</p>
            <p className="pagos-ola__alert-body">
              Si quieres reenviárselo por WhatsApp, este es el enlace. <strong>No lo verás otra vez.</strong>
            </p>
            <p className="pagos-ola__mono pagos-ola__mt-sm" style={{ color: 'inherit' }}>{link}</p>
          </div>
        )}
        {msg && <p className="pagos-ola__msg">{msg}</p>}
      </section>

      {ok && <p className="pagos-ola__msg pagos-ola__msg--ok">{ok}</p>}

      <div>
        <h3 className="pagos-ola__h3">
          Destinos guardados ({data.destinos.length})
        </h3>
        <p className="pagos-ola__hint pagos-ola__hint--tight pagos-ola__hint--xs">
          Aquí quedan todas las cuentas y billeteras a las que ya le has pagado.
          Las que uses en la planilla van directo, sin volver a probarlas.
        </p>
        <div className="pagos-ola__list pagos-ola__mt">
          {data.destinos.length === 0 && (
            <p className="pagos-ola__hint">
              Todavía no hay ninguno. Se guardan solos cuando pagas por primera vez desde la planilla,
              o puedes invitar a alguien arriba para que registre el suyo.
            </p>
          )}
          {data.destinos.map((d) => {
            const e = ESTADO[d.estado] || ESTADO.invitado;
            return (
              <div key={d.id} className={`pagos-ola__card${e.tone === 'bad' ? ' pagos-ola__card--bad' : e.tone === 'warn' ? ' pagos-ola__card--warn' : e.tone === 'good' ? ' pagos-ola__card--good' : ''}`}>
                <div className="pagos-ola__card-row">
                  <div className="min-w-0">
                    <div className="pagos-ola__name">{d.nombre}</div>
                    <div className="pagos-ola__meta">
                      {d.email}{d.pais ? ` · ${d.pais}` : ''}{d.concepto ? ` · ${d.concepto}` : ''}
                    </div>
                    {d.direccion && (
                      <div className="pagos-ola__mono">{d.direccion}</div>
                    )}
                  </div>
                  <span className={`pagos-ola__chip pagos-ola__chip--${e.tone}`}>{e.txt}</span>
                </div>

                <div className="pagos-ola__actions">
                  {d.estado === 'por_probar' && (
                    <button type="button" onClick={() => accion(enviarPrueba, d.id)} disabled={enviando === d.id}
                      className="pagos-ola__btn pagos-ola__btn--primary pagos-ola__btn--sm">
                      {enviando === d.id ? 'Enviando…' : `Enviar US$${data.montoPrueba} de prueba`}
                    </button>
                  )}
                  {d.estado !== 'bloqueado' && (
                    // No mueve un peso: solo le manda otra vez su enlace. Existe
                    // porque el token se guarda hasheado — si perdio el correo, la
                    // unica salida era reenviarle la prueba y gastar otro dolar.
                    <button type="button" onClick={() => accion(reenviarEnlaceFreelancer, d.id)}
                      className="pagos-ola__btn pagos-ola__btn--outline pagos-ola__btn--sm">
                      Reenviarle su enlace
                    </button>
                  )}
                  {d.estado !== 'bloqueado' ? (
                    <button type="button" onClick={() => accion(bloquearFreelancer, d.id, true)}
                      className="pagos-ola__btn pagos-ola__btn--ghost pagos-ola__btn--sm">
                      Bloquear
                    </button>
                  ) : (
                    <button type="button" onClick={() => accion(bloquearFreelancer, d.id, false)}
                      className="pagos-ola__btn pagos-ola__btn--ghost pagos-ola__btn--sm">
                      Desbloquear (vuelve a la prueba)
                    </button>
                  )}
                </div>

                {d.estado === 'por_probar' && (
                  <p className="pagos-ola__hint pagos-ola__hint--tight">
                    Ya registró su dirección de Solana. Mándale la prueba con un clic: sale de tu
                    saldo, le llega un correo, y cuando él confirme que la vio queda habilitado.
                    Le llegarán {(data.montoPrueba * 0.995).toFixed(3)} USDC — se descuenta la
                    comisión del 0,5%.
                  </p>
                )}
                {d.estado === 'probando' && (
                  <>
                    <p className="pagos-ola__hint pagos-ola__hint--tight" style={{ color: 'var(--pg-warn)' }}>
                      La prueba ya salió y le llegó un correo con el botón para confirmar.
                      Falta que <strong>él</strong> confirme que la vio. Si no le llegó, que no
                      confirme — para eso es la prueba.
                    </p>
                    {linkPrueba[d.id] && (
                      <div className="pagos-ola__soft pagos-ola__mt-sm">
                        <div>
                          Su enlace para confirmar, por si prefieres mandárselo por WhatsApp:
                        </div>
                        <div className="pagos-ola__mono">{linkPrueba[d.id]}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============ PAGAR ============ */

function Pagar({ recargar }) {
  const [texto, setTexto] = useState('');
  const [etiqueta, setEtiqueta] = useState('');
  const [prev, setPrev] = useState(null);
  const [lote, setLote] = useState(null);
  const [paso, setPaso] = useState('pegar');   // pegar | revisar | codigo | resultado
  // El celular NO se escribe: el servidor manda el codigo al numero registrado.
  // Aqui solo se guardan los ultimos 4 que devuelve, para poder mostrarlos.
  const [telFinal, setTelFinal] = useState('');
  const [codigo, setCodigo] = useState('');
  const [actionToken, setActionToken] = useState('');
  const [hist, setHist] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [archivo, setArchivo] = useState('');

  useEffect(() => { getDispersiones().then((d) => setHist(d.lotes || [])).catch(() => {}); }, []);

  function leerArchivo(f) {
    setMsg('');
    const r = new FileReader();
    r.onload = () => {
      setTexto(String(r.result || '').trim());
      setArchivo(f.name);
      if (!etiqueta) setEtiqueta(f.name.replace(/\.(csv|txt)$/i, ''));
    };
    r.onerror = () => setMsg('No pude leer ese archivo.');
    // Latin-1 por defecto: los CSV de Excel en español traen tildes y ñ, y
    // leerlos como UTF-8 los rompe.
    r.readAsText(f, 'ISO-8859-1');
  }

  const EJEMPLO = `Maria Perez,maria@ejemplo.com,500,USD,Diseno julio
Joao Silva,joao@ejemplo.com,3200,BRL,Desarrollo julio
Juan Gomez,juan@ejemplo.com,2500000,COP,Nomina julio`;

  async function previsualizar() {
    setMsg(''); setBusy(true);
    try { setPrev(await previsualizarPlantilla(texto)); setPaso('revisar'); }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude leer la lista.'); }
    finally { setBusy(false); }
  }
  async function crear() {
    setMsg(''); setBusy(true);
    try { const r = await crearDispersion({ etiqueta, texto }); setLote(r.lote); setPaso('codigo'); }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude crear el lote.'); }
    finally { setBusy(false); }
  }
  async function pedirCodigo() {
    setMsg(''); setBusy(true);
    try {
      const r = await pedirCodigoDispersion(lote.id);
      setActionToken(r.actionToken);
      setTelFinal(r.telefonoFinal || '');
      setMsg(r.telefonoFinal ? `Te enviamos el codigo al celular terminado en ${r.telefonoFinal}.` : 'Te enviamos el codigo por SMS.');
    }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude enviar el codigo.'); }
    finally { setBusy(false); }
  }
  async function dispersar() {
    setMsg(''); setBusy(true);
    try {
      const r = await ejecutarDispersion(lote.id, { actionToken, codigo });
      setLote(r.lote || r);
      setPaso('resultado');
      // El backend sigue enviando linea por linea: se consulta el estado hasta
      // que termine. Antes se esperaba la respuesta completa y con una nomina
      // grande el navegador cortaba antes de que acabara.
      seguirProgreso(lote.id);
    } catch (e) { setMsg(e?.response?.data?.message || 'No pude dispersar.'); }
    finally { setBusy(false); }
  }

  /**
   * ABRE UN LOTE QUE YA EXISTE.
   *
   * Faltaba: esta pantalla asumia que el lote SIEMPRE se creaba aqui, pegando
   * la planilla. Cuando el Oraculo empezo a armarlos, el lote aparecia en la
   * lista de abajo y no habia forma de abrirlo — Ivan lo vio con el cliente
   * enfrente: "aparece que se creo pero no abre para poder hacer el pago".
   *
   * Segun el estado, se lleva al paso que corresponde: un borrador va al codigo
   * SMS (falta aprobarlo), uno ya disperso va al resultado.
   */
  async function abrirLote(id) {
    // OJO: este componente usa `msg` para los errores, NO `err` — ese vive en
    // el componente de contratistas, mas arriba en el mismo archivo. Llamar a
    // setErr aqui reventaba en runtime y la pantalla se quedaba "leyendo" sin
    // decir nada: el catch tambien fallaba al intentar reportar el fallo.
    setBusy(true); setMsg('');
    try {
      // OJO CON LA FORMA DE LA RESPUESTA: la ruta hace `data: r.lote || r`, o
      // sea DESENVUELVE el lote. Aqui llega el lote directo, no {lote}. Buscar
      // d.lote daba siempre undefined y por eso "no se puede abrir ese lote"
      // con el backend respondiendo perfecto (verificado: estado borrador, 10
      // lineas, USD 2170.1). Se aceptan las dos formas por si la ruta cambia.
      const d = await getLoteDispersion(id);
      const nuevo = d?.lote || d;
      if (!nuevo?.id) {
        setMsg(d?.error || d?.message || 'No pude abrir ese lote.');
        return;
      }
      setLote(nuevo);
      setActionToken('');
      setCodigo('');
      const e = nuevo.estado;
      if (e === 'borrador' || e === 'esperando_codigo') setPaso('codigo');
      else {
        setPaso('resultado');
        if (e === 'dispersando') seguirProgreso(id);
      }
    } catch (e) {
      setMsg(e?.response?.data?.message || 'No pude abrir ese lote.');
    } finally { setBusy(false); }
  }

  /** Consulta el lote cada 3s mientras siga dispersando. */
  function seguirProgreso(id) {
    let intentos = 0;
    const t = setInterval(async () => {
      intentos += 1;
      try {
        const d = await getLoteDispersion(id);
        const act = d?.lote || d;   // misma trampa: la ruta desenvuelve el lote
        if (act?.id) setLote(act);
        const sigue = act?.estado === 'dispersando';
        // Tope de ~15 min: si algo se cuelga, no queda un intervalo eterno.
        if (!sigue || intentos > 300) {
          clearInterval(t);
          getDispersiones().then((x) => setHist(x.lotes || []));
          recargar();
        }
      } catch { /* un fallo de red no debe matar el seguimiento */ }
    }, 3000);
  }
  /** Confirma que la prueba llego y manda lo retenido. */
  async function liberar(lineaId) {
    setMsg(''); setBusy(true);
    try { setLote(await liberarRetenido(lote.id, lineaId)); recargar(); }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude liberar lo retenido.'); }
    finally { setBusy(false); }
  }

  async function reintentar() {
    setBusy(true);
    try { setLote(await reintentarDispersion(lote.id)); }
    catch (e) { setMsg(e?.response?.data?.message || 'No pude reintentar.'); }
    finally { setBusy(false); }
  }
  function empezarDeNuevo() {
    setPaso('pegar'); setPrev(null); setLote(null); setTexto(''); setCodigo(''); setActionToken('');
  }

  const orden = PASOS.map(([k]) => k);
  const idxPaso = orden.indexOf(paso);

  return (
    <>
      <div className="pagos-ola__steps" aria-label="Pasos del pago">
        {PASOS.map(([k, t], i) => (
          <span key={k}
            className={`pagos-ola__step${i === idxPaso ? ' pagos-ola__step--on' : ''}${i < idxPaso ? ' pagos-ola__step--done' : ''}`}>
            {i + 1}. {t}
          </span>
        ))}
      </div>

      {paso === 'pegar' && (
        <section className="pagos-ola__panel">
          <h2 className="pagos-ola__h2">Tu lista de pagos</h2>
          <p className="pagos-ola__hint">
            Descarga la plantilla, llénala en Excel y súbela. Trae un ejemplo de cada
            destino: <strong>banco</strong>, <strong>cuenta FlowexPay</strong> y <strong>billetera</strong>.
          </p>

          <button type="button" onClick={downloadTemplate}
            className="pagos-ola__btn pagos-ola__btn--outline pagos-ola__btn--mt">
            ⬇ Descargar plantilla
          </button>

          <div className="pagos-ola__soft pagos-ola__mt">
            <div className="pagos-ola__soft-title">Qué columnas llena cada destino</div>
            <table className="pagos-ola__table">
              <tbody>
                <tr>
                  <td>banco</td>
                  <td>
                    Llena <span className="pagos-ola__mono" style={{ display: 'inline' }}>banco</span>,{' '}
                    <span className="pagos-ola__mono" style={{ display: 'inline' }}>tipo_cuenta</span> y{' '}
                    <span className="pagos-ola__mono" style={{ display: 'inline' }}>numero_cuenta</span>. Deja{' '}
                    <span className="pagos-ola__mono" style={{ display: 'inline' }}>referencia</span> vacía.
                    <div className="dim">BANCOLOMBIA · ahorros · 12345678901</div>
                  </td>
                </tr>
                <tr>
                  <td>flowexpay</td>
                  <td><span className="pagos-ola__mono" style={{ display: 'inline' }}>referencia</span> = el correo de su cuenta
                    <div className="dim">pedro@correo.com</div>
                  </td>
                </tr>
                <tr>
                  <td>blockchain</td>
                  <td><span className="pagos-ola__mono" style={{ display: 'inline' }}>referencia</span> = su dirección de Solana
                    <div className="dim">9Spodq6CmXasjYV4Yvau…</div>
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="pagos-ola__hint pagos-ola__hint--tight">
              Para <strong>banco</strong> el <span className="pagos-ola__mono" style={{ display: 'inline' }}>documento</span> es obligatorio:
              el banco dispersa contra la cédula del titular, no solo contra el número de cuenta.
              El nombre del banco lo reconocemos como lo escribas — «nequi», «colpatria», «Banco de Bogotá» —
              y si no lo encontramos te lo decimos antes de pagar, con los parecidos.
            </p>
            <p className="pagos-ola__hint pagos-ola__hint--tight">
              Solo <strong>flowexpay</strong> necesita que la persona tenga cuenta.
              A un banco o a una billetera le pagas aunque no esté registrada.
            </p>
          </div>

          <input value={etiqueta} onChange={(e) => setEtiqueta(e.target.value)}
            placeholder="Etiqueta (ej. Quincena 1 · agosto)"
            className="pagos-ola__input pagos-ola__mt" />

          {/* Primero PREGUNTAR, no ensenar el formato. Quien ya sabe sigue
              subiendo su archivo; quien es primerizo arma la lista aqui. */}
          <div className="pagos-ola__mt">
            <ArmadorDeLista onListo={(csv) => { setTexto(csv); setMsg(''); }} />
          </div>

          {/* Subir el archivo, no solo pegar. Descargar un CSV, llenarlo en Excel
              y despues tener que abrirlo para copiar el contenido no tiene sentido. */}
          <label className="pagos-ola__drop pagos-ola__mt"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) leerArchivo(f); }}>
            <input type="file" accept=".csv,.txt,text/csv,text/plain" className="pagos-ola__hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) leerArchivo(f); }} />
            <span>
              📄 <strong>Sube tu archivo</strong> o arrástralo aquí
              <span className="dim"> · CSV o Excel guardado como CSV</span>
            </span>
          </label>
          {archivo && <p className="pagos-ola__ok-file">✓ {archivo}</p>}

          <div className="pagos-ola__divider">o pega las filas directo</div>

          <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={7}
            placeholder={EJEMPLO}
            className="pagos-ola__textarea pagos-ola__mt-sm" />

          <div className="pagos-ola__actions">
            <button type="button" onClick={() => setTexto(EJEMPLO)} className="pagos-ola__text-btn">
              Usar un ejemplo
            </button>
            <span className="pagos-ola__meta">
              Monedas: USD · COP · BRL · MXN · EUR
            </span>
          </div>

          {/* El nombre del boton tenia que decir que NADA sale todavia. "Ver a
              donde va cada uno" describia la pantalla, no el paso: quien esta
              por mover una nomina necesita saber si ese clic ya paga. */}
          <button type="button" onClick={previsualizar} disabled={!texto.trim() || busy}
            className="pagos-ola__btn pagos-ola__btn--primary pagos-ola__btn--mt">
            {busy ? 'Leyendo…' : 'Revisar antes de pagar'}
          </button>
          <p className="pagos-ola__hint pagos-ola__hint--tight">
            No sale nada todavía. Primero te mostramos a dónde va cada pago y cuánto,
            y después lo confirmas con un código que te llega al celular.
          </p>
          {msg && <p className="pagos-ola__msg">{msg}</p>}
        </section>
      )}

      {paso === 'revisar' && prev && (
        <div className="pagos-ola__block">
          <div className="pagos-ola__alert pagos-ola__alert--info">
            <p className="pagos-ola__alert-title">Revisa antes de pagar</p>
            <p className="pagos-ola__alert-body">
              Esto es lo que vamos a mandar. <strong>Todavía no ha salido nada.</strong> Revisa
              que cada persona vaya a donde tiene que ir y que el monto esté bien; si algo no
              cuadra, corrige la lista y vuelve a subirla.
            </p>
            <p className="pagos-ola__alert-body">
              Al continuar te pedimos un código al celular. Solo después de ese código sale el dinero.
            </p>
          </div>

          <Totales resumen={prev.resumen} />

          {/* DE DONDE SALE LA PLATA. Todo se debita en dolares del saldo de la
              empresa, tambien lo que se paga en pesos: a un banco colombiano van
              dolares y Payvalida los entrega en pesos. Sin esto, el dueno mira
              su saldo en pesos, no le cuadra con el total, y no sabe si le
              alcanza — que es justo lo que no se puede no saber antes de
              aprobar una nomina. */}
          {prev.fondos && (
            <div className={`pagos-ola__fee${prev.fondos.alcanza === false ? ' pagos-ola__fee--bad' : ''}`}>
              <div className="pagos-ola__fee-title">De dónde sale</div>
              <p className="pagos-ola__hint pagos-ola__hint--tight">
                Todo se descuenta hoy del <strong>saldo en dólares</strong> — también lo que pagas
                en pesos: al banco colombiano van dólares y allá se entregan en pesos.
              </p>

              <div className="pagos-ola__fee-grid">
                <div className="pagos-ola__fee-box">
                  <div className="pagos-ola__fee-lbl">Se debita en dólares</div>
                  {/* Sin tasa fresca NO se estima un numero: un total mas bajo
                      que el real parece que alcanza, y eso es peor que no saber. */}
                  <div className="pagos-ola__fee-val">
                    {prev.fondos.necesarioUsd === null ? '—' : `≈ ${usd(prev.fondos.necesarioUsd)}`}
                  </div>
                  {prev.fondos.saldoUsd !== null && (
                    <div className="pagos-ola__fee-sub">tienes {usd(prev.fondos.saldoUsd)}</div>
                  )}
                </div>
                <div className="pagos-ola__fee-box">
                  <div className="pagos-ola__fee-lbl">Vas a pagar</div>
                  {Object.entries(prev.fondos.necesario).map(([m, v]) => (
                    <div key={m} className="pagos-ola__name" style={{ marginTop: '0.15rem' }}>
                      {Number(v).toLocaleString(m === 'COP' ? 'es-CO' : 'en-US',
                        { minimumFractionDigits: m === 'COP' ? 0 : 2 })} {m}
                    </div>
                  ))}
                  {prev.fondos.tasa && (
                    <div className="pagos-ola__fee-sub">
                      1 USD = {Math.round(prev.fondos.tasa).toLocaleString('es-CO')} COP
                    </div>
                  )}
                </div>
              </div>

              {prev.fondos.sinTasa && (
                <p className="pagos-ola__msg">
                  {prev.fondos.motivoSinTasa} Prefiero no darte un número antes que darte uno malo.
                  Intenta en unos minutos.
                </p>
              )}

              {prev.fondos.alcanza === false && (
                <p className="pagos-ola__msg">
                  Te faltan {usd(prev.fondos.faltanUsd)} en dólares. Recarga o convierte antes de
                  aprobar: si arrancas sin fondos, una parte de la nómina sale y la otra no.
                </p>
              )}

              {/* Pesos parados mientras la nomina en pesos se paga con dolares.
                  Se dice aunque todavia no se pueda evitar: el cliente tiene
                  derecho a saber que esta pagando la conversion dos veces. */}
              {prev.fondos.pagaConPesos > 0 && (
                <p className="pagos-ola__hint pagos-ola__hint--tight" style={{ color: 'var(--pg-good)' }}>
                  <strong>{Number(prev.fondos.pagaConPesos).toLocaleString('es-CO')} COP salen de tu saldo
                  en pesos, sin comisión</strong> — no hay cambio de moneda, así que no se cobra nada por
                  esa parte. Te quedan {Number(prev.fondos.saldoCop - prev.fondos.pagaConPesos).toLocaleString('es-CO')} COP.
                </p>
              )}

              {prev.fondos.sinCotizar?.length > 0 && (
                <p className="pagos-ola__hint pagos-ola__hint--tight">
                  {prev.fondos.sinCotizar.join(', ')} no está incluido en el cálculo en dólares: ese
                  riel se liquida aparte.
                </p>
              )}

              {/* El desglose: sin el, el dueno ve un total que no cuadra con el
                  monto de su planilla y no sabe de donde sale la diferencia. */}
              {prev.fondos.desglose && (
                <div className="pagos-ola__fee-rows">
                  <div className="pagos-ola__fee-row">
                    <span>Conversión a dólares</span>
                    <span>{usd(prev.fondos.desglose.conversionUsd)}</span>
                  </div>
                  <div className="pagos-ola__fee-row">
                    <span>
                      Comisión del envío a banco
                      {prev.fondos.desglose.comisionEsMinima
                        ? ' (mínima)'
                        : ` (${prev.fondos.desglose.spreadPct}%)`}
                    </span>
                    <span>{usd(prev.fondos.desglose.comisionUsd)}</span>
                  </div>
                  <div className="pagos-ola__fee-row pagos-ola__fee-row--total">
                    <span>Total que se debita</span>
                    <span>{usd(prev.fondos.necesarioUsd)}</span>
                  </div>
                  {prev.fondos.desglose.comisionEsMinima && (
                    <p className="pagos-ola__hint pagos-ola__hint--tight" style={{ color: 'var(--pg-warn)' }}>
                      En montos pequeños la comisión mínima pesa mucho: aquí es casi un cuarto
                      del envío. Juntar varios pagos en uno sale más barato.
                    </p>
                  )}
                </div>
              )}

              <p className="pagos-ola__hint pagos-ola__hint--tight pagos-ola__hint--xs">{prev.fondos.nota}</p>
            </div>
          )}

          {prev.errores?.length > 0 && (
            <div className="pagos-ola__alert pagos-ola__alert--bad">
              <p className="pagos-ola__alert-title">No pude leer estas lineas</p>
              {prev.errores.map((e, i) => <p key={i} className="pagos-ola__alert-body">{e}</p>)}
            </div>
          )}

          <div className="pagos-ola__list">
            {prev.filas.map((f, i) => (
              <div key={i} className={`pagos-ola__card${f.listo ? ' pagos-ola__card--good' : ' pagos-ola__card--warn'}`}>
                <div className="pagos-ola__card-row">
                  <div className="min-w-0">
                    <div className="pagos-ola__name">{f.nombre}</div>
                    <div className="pagos-ola__meta">{f.correo}{f.concepto ? ` · ${f.concepto}` : ''}</div>
                    <div className={`pagos-ola__meta pagos-ola__mt-sm ${f.listo ? 'pagos-ola__status--good' : 'pagos-ola__status--warn'}`}>
                      {f.listo ? `→ ${f.detalle}` : f.motivo}
                    </div>
                    {/* El pago va al titular del correo, no a quien diga la
                        planilla. Si los nombres no se parecen, se dice aqui —
                        antes de aprobar, que es cuando todavia se puede evitar. */}
                    {f.avisoTitular && (
                      <div className="pagos-ola__alert pagos-ola__alert--warn pagos-ola__mt-sm" style={{ padding: '0.4rem 0.6rem', fontSize: '0.7rem' }}>
                        {f.avisoTitular}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0" style={{ textAlign: 'right' }}>
                    <div className="pagos-ola__amt">
                      {Number(f.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })} {f.moneda}
                    </div>
                    {f.monedaAsumida && <div className="pagos-ola__amt-sub">moneda asumida</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pagos-ola__actions">
            {/* Si no alcanza, NO se deja seguir. Un aviso que se puede ignorar no
                sirve cuando el resultado es media nomina pagada: aqui el sistema
                sabe con certeza que va a fallar, asi que lo dice y para. */}
            <button type="button" onClick={crear} disabled={!prev.resumen.listas || busy || prev.fondos?.alcanza === false}
              className="pagos-ola__btn pagos-ola__btn--primary">
              {busy ? 'Creando…'
                : prev.fondos?.alcanza === false
                  ? `Te falta saldo — recarga ${usd(prev.fondos.faltanUsd)} para continuar`
                  : `Continuar con ${prev.resumen.listas} pago(s) · te pediremos un código`}
            </button>
            <button type="button" onClick={() => setPaso('pegar')} className="pagos-ola__btn pagos-ola__btn--ghost">
              Corregir la lista
            </button>
          </div>
          {prev.resumen.faltan > 0 && (
            <p className="pagos-ola__hint" style={{ color: 'var(--pg-warn)' }}>
              {prev.resumen.faltan} persona(s) no entran todavia. Invitalas desde "Quien puede recibir".
            </p>
          )}
          {msg && <p className="pagos-ola__msg">{msg}</p>}
        </div>
      )}

      {paso === 'codigo' && lote && (
        <section className="pagos-ola__panel">
          <h2 className="pagos-ola__h2">Aprueba con tu codigo</h2>
          <div className="pagos-ola__mt-sm"><Totales resumen={{ listas: lote.resumen.total, porMoneda: lote.totales }} compacto /></div>
          <div className="pagos-ola__alert pagos-ola__alert--bad pagos-ola__alert--mt">
            Los envios por blockchain <strong>no se pueden reversar</strong>. Revisa antes de aprobar.
          </div>

          {/* A DONDE VA CADA PESO — antes de aprobar, no despues.
              Esta pantalla decia "revisa antes de aprobar" y no mostraba NADA
              que revisar: solo el total. Ivan lo vio con el cliente enfrente.
              Aprobar a ciegas un lote de diez destinos, por un riel sin reverso,
              es exactamente lo que el codigo SMS deberia impedir. */}
          <div className="pagos-ola__mt">
            <div className="pagos-ola__h3 pagos-ola__mb">
              A donde va el dinero ({lote.lineas.length} pago{lote.lineas.length === 1 ? '' : 's'})
            </div>
            <div className="pagos-ola__line-list">
              {lote.lineas.map((l) => {
                const via = l.destino === 'banco' ? l.banco
                  : l.destino === 'flowexpay' ? 'cuenta FlowexPay' : 'billetera';
                // Cuenta y direccion ACORTADAS: alcanza para reconocerlas y no
                // deja el numero completo a la vista de quien pase por detras.
                const ref = l.destino === 'banco'
                  ? `…${String(l.numero || '').slice(-4)}`
                  : l.destino === 'flowexpay'
                    ? l.referencia
                    : `${String(l.referencia || '').slice(0, 4)}…${String(l.referencia || '').slice(-4)}`;
                return (
                  <div key={l.id} className="pagos-ola__line">
                    <div className="min-w-0">
                      <div className="pagos-ola__name" style={{ fontSize: '0.8125rem' }}>{l.nombre}</div>
                      <div className="pagos-ola__meta">{via} · {ref}</div>
                      {l.esNueva && (
                        <div className="pagos-ola__meta pagos-ola__status--warn pagos-ola__mt-sm">
                          Direccion nueva: sale US$1 de prueba y el resto queda retenido
                        </div>
                      )}
                    </div>
                    <div className="pagos-ola__amt shrink-0">
                      {l.moneda === 'COP'
                        ? `$${Math.round(l.monto).toLocaleString('es-CO')} COP`
                        : `$${Number(l.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* NO se pide el numero. El codigo va al celular REGISTRADO de la
              cuenta, que solo conoce el servidor: si la pantalla pudiera
              elegirlo, quien tenga la sesion abierta pondria el suyo y el SMS
              dejaria de verificar nada. */}
          <div className="pagos-ola__mt">
            <button type="button" onClick={pedirCodigo} disabled={busy}
              className="pagos-ola__btn pagos-ola__btn--outline">
              {busy ? 'Enviando…' : 'Enviarme el código al celular registrado'}
            </button>
            <p className="pagos-ola__hint pagos-ola__hint--tight">
              {telFinal
                ? `El código va al celular de tu cuenta, terminado en ${telFinal}.`
                : 'El código va al celular registrado en tu cuenta. No se puede cambiar desde aquí, por seguridad.'}
            </p>
          </div>

          {actionToken && (
            <div className="pagos-ola__mt">
              <label className="pagos-ola__label">Codigo</label>
              <div className="pagos-ola__actions" style={{ marginTop: '0.35rem' }}>
                <input value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric" placeholder="000000"
                  className="pagos-ola__input pagos-ola__input--code" />
                <button type="button" onClick={dispersar} disabled={codigo.length !== 6 || busy}
                  className="pagos-ola__btn pagos-ola__btn--primary">
                  {busy ? 'Dispersando…' : 'Dispersar'}
                </button>
              </div>
              {busy && <p className="pagos-ola__hint pagos-ola__hint--tight">Se envian uno por uno. No cierres esta pantalla.</p>}
            </div>
          )}
          {msg && <p className="pagos-ola__msg pagos-ola__msg--neutral">{msg}</p>}
        </section>
      )}

      {paso === 'resultado' && lote && (
        <div className="pagos-ola__block">
          <Resultado lote={lote} onReintentar={reintentar} busy={busy} onLiberar={liberar} />
          <button type="button" onClick={empezarDeNuevo} className="pagos-ola__text-btn">
            Hacer otra dispersion
          </button>
        </div>
      )}

      {hist.length > 0 && paso === 'pegar' && (
        <div>
          <h3 className="pagos-ola__h3">Dispersiones anteriores</h3>
          <div className="pagos-ola__list pagos-ola__mt">
            {hist.slice(0, 8).map((l) => {
              const esperando = l.estado === 'borrador' || l.estado === 'esperando_codigo';
              return (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => abrirLote(l.id)}
                  disabled={busy}
                  className={`pagos-ola__hist-btn${esperando ? ' pagos-ola__hist-btn--wait' : ''}`}
                >
                  <div className="min-w-0">
                    <div className="pagos-ola__name" style={{ fontSize: '0.8125rem' }}>{l.etiqueta}</div>
                    <div className="pagos-ola__meta">
                      {esperando
                        ? `${l.resumen.total} pago(s) · esperando tu aprobación`
                        : <>
                            {l.resumen.enviadas}/{l.resumen.total} enviados
                            {l.resumen.fallidas ? ` · ${l.resumen.fallidas} fallaron` : ''}
                          </>}
                    </div>
                  </div>
                  <div className="pagos-ola__meta" style={{ textAlign: 'right', flexShrink: 0 }}>
                    {Object.entries(l.totales || {}).map(([m, v]) => (
                      <div key={m}>{Number(v).toLocaleString('en-US')} {m}</div>
                    ))}
                    <div className={`pagos-ola__hist-cta${esperando ? ' pagos-ola__hist-cta--wait' : ''}`}>
                      {esperando ? 'Aprobar →' : 'Ver →'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

/** Totales SIEMPRE por moneda. Sumarlas daria un numero que parece bueno y no lo es. */
function Totales({ resumen, compacto }) {
  const monedas = Object.entries(resumen?.porMoneda || {});
  return (
    <div className="pagos-ola__totales">
      {!compacto && (
        <div className="pagos-ola__meta" style={{ fontSize: '0.8125rem', color: 'var(--pg-text)' }}>
          <strong>{resumen.listas}</strong> listo(s) para pagar
          {resumen.faltan > 0 && <> · <span className="pagos-ola__status--warn">{resumen.faltan} pendiente(s)</span></>}
          {resumen.revisarMoneda > 0 && <> · <span className="pagos-ola__status--bad">{resumen.revisarMoneda} con moneda que no calza</span></>}
        </div>
      )}
      <div className="pagos-ola__totales-row">
        {monedas.length === 0 && <span className="pagos-ola__meta">Nada por pagar todavia.</span>}
        {monedas.map(([m, v]) => (
          <div key={m}>
            <div className="pagos-ola__totales-m">{m}</div>
            <div className="pagos-ola__totales-v">{Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          </div>
        ))}
      </div>
      {monedas.length > 1 && (
        <p className="pagos-ola__hint pagos-ola__hint--tight">Cada moneda va por su riel. No se suman entre si.</p>
      )}
    </div>
  );
}

function Resultado({ lote, onReintentar, busy, onLiberar }) {
  const hayFallos = lote.resumen.fallidas > 0;
  // "Revisar" NO es lo mismo que "falló": el banco respondió bien pero no
  // reconocimos la referencia, así que el pago pudo haber salido. Se separa
  // porque la accion correcta es opuesta — ante un fallo se reintenta, ante una
  // duda se mira el movimiento ANTES de tocar nada.
  const porRevisar = lote.resumen.revisar || 0;
  const hayDudas = porRevisar > 0;
  // Mientras el backend sigue enviando, decirlo. Antes ponia "Listo: 0 pago(s)
  // enviados" con las lineas todavia en curso — se leia como que no habia hecho
  // nada, justo cuando estaba saliendo la plata.
  const enCurso = lote.estado === 'dispersando';
  const pendientes = lote.resumen.pendientes || 0;
  const tone = enCurso ? 'info' : (hayFallos || hayDudas ? 'warn' : 'ok');
  return (
    <div className={`pagos-ola__alert pagos-ola__alert--${tone}`} style={{ padding: '1.1rem 1.15rem' }}>
      <p className="pagos-ola__alert-title">
        {enCurso
          ? `Enviando… ${lote.resumen.enviadas} de ${lote.resumen.total} · quedan ${pendientes}`
          : hayFallos || hayDudas
            ? [`${lote.resumen.enviadas} enviados`,
              hayFallos ? `${lote.resumen.fallidas} fallaron` : '',
              hayDudas ? `${porRevisar} por revisar` : ''].filter(Boolean).join(', ')
            : `Listo: ${lote.resumen.enviadas} pago(s) enviados`}
      </p>
      {lote.resumen.enPrueba > 0 && !enCurso && (
        <div className="pagos-ola__alert pagos-ola__alert--info pagos-ola__alert--mt">
          <p className="pagos-ola__alert-title">
            {lote.resumen.enPrueba} dirección(es) nueva(s): les mandamos US$1 de prueba
          </p>
          <p className="pagos-ola__alert-body">
            El resto quedó retenido hasta que confirmes que llegó. Pregúntale a la persona si lo
            recibió y dale al botón verde. <strong>Solo pasa la primera vez</strong>: desde el
            próximo pago esa dirección va directa.
          </p>
        </div>
      )}

      {enCurso && (
        <p className="pagos-ola__alert-body">
          Sale uno a la vez, a propósito. Puedes cerrar esta pantalla: el envío sigue igual.
        </p>
      )}

      {hayDudas && (
        <div className="pagos-ola__alert pagos-ola__alert--bad pagos-ola__alert--mt">
          <p className="pagos-ola__alert-title">Antes de reintentar, revisa estos</p>
          <p className="pagos-ola__alert-body">
            El pago pudo haber salido: el banco respondió bien pero no pudimos leer la referencia.
            Míralo en tus movimientos. <strong>Si ya salió y lo reintentas, pagas dos veces</strong> —
            y por blockchain no hay devolución.
          </p>
        </div>
      )}

      <div className="pagos-ola__list pagos-ola__mt">
        {lote.lineas.map((l) => (
          <div key={l.id} className="pagos-ola__card">
            <div className="pagos-ola__card-row">
              <div className="min-w-0">
                <div className="pagos-ola__name" style={{ fontSize: '0.8125rem' }}>{l.nombre}</div>
                <div className="pagos-ola__meta">{l.riel}</div>
                {l.error && <div className="pagos-ola__meta pagos-ola__status--bad pagos-ola__mt-sm">{l.error}</div>}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div className="pagos-ola__amt">
                  {Number(l.monto).toLocaleString('en-US', { minimumFractionDigits: 2 })} {l.moneda}
                </div>
                {/* Direccion nueva: recibio la prueba y el resto quedo retenido.
                    Sin este boton no habia forma de liberarlo — el backend sabia
                    hacerlo, la pantalla nunca se lo pedia. */}
                {l.estado === 'en_prueba' && l.montoRetenido > 0 && (
                  <button type="button" onClick={() => onLiberar(l.id)} disabled={busy}
                    className="pagos-ola__btn pagos-ola__btn--good pagos-ola__btn--sm pagos-ola__mt-sm">
                    Le llegó — enviar {Number(l.montoRetenido).toLocaleString('en-US', { minimumFractionDigits: 2 })} {l.moneda}
                  </button>
                )}
                <div className={`pagos-ola__status${
                  l.estado === 'enviado' ? ' pagos-ola__status--good'
                    : l.estado === 'fallo' ? ' pagos-ola__status--bad'
                      : l.estado === 'revisar' ? ' pagos-ola__status--warn' : ''}`}>
                  {l.estado === 'enviado' ? 'enviado'
                    : l.estado === 'fallo' ? 'falló'
                      : l.estado === 'revisar' ? 'por revisar' : l.estado}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {hayFallos && !enCurso && (
        <>
          <button type="button" onClick={onReintentar} disabled={busy}
            className="pagos-ola__btn pagos-ola__btn--primary pagos-ola__btn--mt">
            {busy ? 'Reintentando…' : 'Reintentar solo las que fallaron'}
          </button>
          <p className="pagos-ola__hint pagos-ola__hint--tight" style={{ color: 'var(--pg-warn)' }}>
            Las que ya salieron no se vuelven a enviar. Solo se reintentan las fallidas —
            las marcadas «por revisar» tampoco se tocan, justamente porque podrían haber salido.
          </p>
        </>
      )}
    </div>
  );
}

/* ============ ARMAR LA LISTA PREGUNTANDO ============ */

/**
 * ArmadorDeLista — para quien nunca ha llenado la planilla.
 *
 * La idea de Ivan: no hay que ENSENAR el formato, hay que PREGUNTAR. La primera
 * pregunta no es "que columnas lleva" sino la unica que el dueno ya sabe
 * responder: **como le vas a pagar a esta persona** — banco, billetera o cuenta
 * FlowexPay. De ahi se piden SOLO los datos de ese camino: al de blockchain no
 * se le pregunta banco, al de banco no se le pide direccion de Solana.
 *
 * Al final arma el CSV solo. Quien ya sabe, sigue subiendo su archivo.
 */
function ArmadorDeLista({ onListo }) {
  const [destino, setDestino] = useState('');
  const [f, setF] = useState({});
  const [filas, setFilas] = useState([]);
  const [error, setError] = useState('');

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const limpiar = () => { setF({}); setDestino(''); setError(''); };

  // Cada destino pide lo suyo y NADA mas. Pedir de sobra es lo que hace que
  // alguien abandone en la fila 3.
  const CAMPOS = {
    banco: [
      ['nombre', 'Nombre', 'María'], ['apellido', 'Apellidos', 'Gómez Ruiz'],
      ['documento', 'Cédula', '1023456789'], ['celular', 'Celular', '3001234567'],
      ['banco', 'Banco', 'Bancolombia · Nequi · Davivienda…'],
      ['numero_cuenta', 'Número de cuenta', '12345678901'],
      ['monto', 'Cuánto recibe (COP)', '500000'],
    ],
    blockchain: [
      ['nombre', 'Nombre', 'Ana Díaz'],
      ['referencia', 'Su dirección de USDC en Solana', 'La saca de su billetera: Recibir → USDC → Solana'],
      ['monto', 'Cuánto recibe (USD)', '200'],
    ],
    flowexpay: [
      ['nombre', 'Nombre', 'Pedro Ruiz'],
      ['referencia', 'Correo de su cuenta FlowexPay', 'pedro@correo.com'],
      ['monto', 'Cuánto recibe (USD)', '200'],
    ],
  };

  function agregar() {
    setError('');
    const faltan = CAMPOS[destino].filter(([k]) => !String(f[k] || '').trim()).map(([, l]) => l);
    if (faltan.length) { setError(`Falta: ${faltan.join(', ')}`); return; }
    setFilas([...filas, { ...f, destino, tipo_cuenta: f.tipo_cuenta || 'ahorros', concepto: f.concepto || 'Pago' }]);
    limpiar();
  }

  function usarLista() {
    const cab = 'documento,tipo_doc,nombre,apellido,celular,destino,referencia,banco,tipo_cuenta,numero_cuenta,moneda,monto,concepto';
    const esc = (v) => String(v || '').replace(/,/g, ' ');
    const lineas = filas.map((r) => [
      esc(r.documento), 'CC', esc(r.nombre), esc(r.apellido), esc(r.celular),
      r.destino, esc(r.referencia), esc(r.banco), r.destino === 'banco' ? r.tipo_cuenta : '',
      esc(r.numero_cuenta), r.destino === 'banco' ? 'COP' : 'USD', String(r.monto).replace(/[^\d.]/g, ''),
      esc(r.concepto),
    ].join(','));
    onListo([cab, ...lineas].join('\n'));
  }

  return (
    <div className="pagos-ola__soft" style={{ background: 'color-mix(in srgb, var(--pg-muted) 6%, var(--pg-card))' }}>
      <div className="pagos-ola__soft-title" style={{ fontSize: '0.8125rem' }}>¿Es tu primera vez? Ármala aquí</div>
      <p className="pagos-ola__hint pagos-ola__hint--tight">
        Te preguntamos persona por persona. No necesitas saber cómo se llena el archivo.
      </p>

      {filas.length > 0 && (
        <div className="pagos-ola__list pagos-ola__mt" style={{ gap: '0.35rem' }}>
          {filas.map((r, i) => (
            <div key={i} className="pagos-ola__mini">
              <span>
                <strong>{r.nombre}</strong> · {r.destino === 'banco' ? `${r.banco} ...${String(r.numero_cuenta).slice(-4)}`
                  : r.destino === 'flowexpay' ? r.referencia : `billetera ${String(r.referencia).slice(0, 6)}…`}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontWeight: 700 }}>
                  {Number(r.monto).toLocaleString(r.destino === 'banco' ? 'es-CO' : 'en-US')} {r.destino === 'banco' ? 'COP' : 'USD'}
                </span>
                <button type="button" onClick={() => setFilas(filas.filter((_, j) => j !== i))}
                  className="pagos-ola__text-btn pagos-ola__text-btn--danger">quitar</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {!destino ? (
        <div className="pagos-ola__mt">
          <p className="pagos-ola__label pagos-ola__mb" style={{ color: 'var(--pg-text)', fontWeight: 700 }}>
            {filas.length ? 'Agrega a otra persona:' : '¿Cómo le vas a pagar a esta persona?'}
          </p>
          <div className="pagos-ola__grid-3">
            {[['banco', 'A un banco en Colombia', 'Bancolombia, Nequi, Davivienda…'],
              ['blockchain', 'A una billetera', 'USDC por Solana · Binance, Phantom'],
              ['flowexpay', 'A una cuenta FlowexPay', 'Con su correo, sin comisión']].map(([k, t, d]) => (
              <button key={k} type="button" onClick={() => setDestino(k)}
                className="pagos-ola__choice">
                <div className="pagos-ola__choice-t">{t}</div>
                <div className="pagos-ola__choice-d">{d}</div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="pagos-ola__mt">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p className="pagos-ola__label" style={{ color: 'var(--pg-text)', fontWeight: 700 }}>
              {destino === 'banco' ? 'Pago a un banco en Colombia'
                : destino === 'blockchain' ? 'Pago a una billetera (USDC por Solana)'
                  : 'Pago a una cuenta FlowexPay'}
            </p>
            <button type="button" onClick={limpiar} className="pagos-ola__text-btn pagos-ola__text-btn--muted">cambiar</button>
          </div>
          <div className="pagos-ola__grid-2 pagos-ola__mt-sm">
            {CAMPOS[destino].map(([k, label, ph]) => (
              <div key={k}>
                <label className="pagos-ola__label">{label}</label>
                <input value={f[k] || ''} onChange={set(k)} placeholder={ph}
                  className="pagos-ola__input" style={{ marginTop: '0.2rem' }} />
              </div>
            ))}
            {destino === 'banco' && (
              <div>
                <label className="pagos-ola__label">Tipo de cuenta</label>
                <select value={f.tipo_cuenta || 'ahorros'} onChange={set('tipo_cuenta')}
                  className="pagos-ola__select" style={{ marginTop: '0.2rem' }}>
                  <option value="ahorros">Ahorros</option>
                  <option value="corriente">Corriente</option>
                </select>
              </div>
            )}
            <div>
              <label className="pagos-ola__label">Concepto</label>
              <input value={f.concepto || ''} onChange={set('concepto')} placeholder="Nómina quincena"
                className="pagos-ola__input" style={{ marginTop: '0.2rem' }} />
            </div>
          </div>
          {destino === 'blockchain' && (
            <p className="pagos-ola__hint pagos-ola__hint--tight" style={{ color: '#f87171' }}>
              Tiene que ser la red <strong>Solana</strong>. Si es de otra red el dinero se pierde;
              por eso la primera vez le mandamos US$1 de prueba.
            </p>
          )}
          {error && <p className="pagos-ola__msg">{error}</p>}
          <button type="button" onClick={agregar}
            className="pagos-ola__btn pagos-ola__btn--primary pagos-ola__btn--sm pagos-ola__btn--mt">
            Agregar a la lista
          </button>
        </div>
      )}

      {filas.length > 0 && !destino && (
        <button type="button" onClick={usarLista}
          className="pagos-ola__btn pagos-ola__btn--good pagos-ola__btn--block pagos-ola__btn--mt">
          Usar esta lista ({filas.length} {filas.length === 1 ? 'persona' : 'personas'})
        </button>
      )}
    </div>
  );
}
